import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { createEmployee, listEmployees, updateEmployee } from "../api/employees.js";
import { ApiRequestError } from "../api/client.js";
import { useApiClient } from "../api/ApiClientContext.js";
import type { PublicEmployee } from "../api/types.js";
import { describeApiError } from "../domain/errors.js";

/**
 * Add / list / deactivate the shop's employees — the PIN people every intake,
 * pickup and deletion is attributed to. Shared by the setup wizard's "add
 * users" step and the settings screen, since they are the same task.
 *
 * A PIN is never shown once set (the list is the code-less `PublicEmployee`
 * shape): the operator adds a new employee or deactivates an old one, but
 * cannot read an existing PIN back. Deactivation, not deletion, is the removal
 * path — an inactive employee still has to exist for the audit trail that
 * references them (matches `employees.active`).
 */
export function EmployeeManager() {
  const client = useApiClient();
  const queryClient = useQueryClient();

  const employeesQuery = useQuery({ queryKey: ["employees"], queryFn: () => listEmployees(client) });

  const [name, setName] = useState("");
  const [lastname, setLastname] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["employees"] });

  const createMutation = useMutation({
    mutationFn: () => createEmployee(client, { name: name.trim(), lastname: lastname.trim(), code: code.trim() }),
    onSuccess: async () => {
      setName("");
      setLastname("");
      setCode("");
      setError(null);
      await invalidate();
    },
    onError: (err) => {
      // The duplicate-PIN 409 is the one an operator will actually hit, so it gets plain wording.
      const isDuplicate = err instanceof ApiRequestError && err.failure.kind === "conflict" && err.failure.reason === "duplicate-code";
      setError(isDuplicate ? "Ta PIN je že v uporabi. Izberite drugega." : describeApiError(err));
    },
  });

  const toggleMutation = useMutation({
    mutationFn: (employee: PublicEmployee) => updateEmployee(client, employee.id, { active: !employee.active }),
    onSuccess: () => invalidate(),
  });

  function handleAdd(event: FormEvent) {
    event.preventDefault();
    if (createMutation.isPending) return;
    createMutation.mutate();
  }

  const employees = employeesQuery.data?.employees ?? [];

  return (
    <div className="employee-manager">
      <form className="employee-add" onSubmit={handleAdd}>
        <div className="employee-add-fields">
          <label>
            <span>Ime</span>
            <input value={name} onChange={(e) => setName(e.target.value)} required autoComplete="off" />
          </label>
          <label>
            <span>Priimek</span>
            <input value={lastname} onChange={(e) => setLastname(e.target.value)} required autoComplete="off" />
          </label>
          <label>
            <span>PIN</span>
            <input value={code} onChange={(e) => setCode(e.target.value)} required inputMode="numeric" minLength={2} maxLength={8} autoComplete="off" />
          </label>
          <button type="submit" disabled={createMutation.isPending}>
            {createMutation.isPending ? "Dodajanje …" : "Dodaj"}
          </button>
        </div>
        {error && (
          <p className="settings-error" role="alert">
            {error}
          </p>
        )}
      </form>

      {employees.length === 0 ? (
        <p className="employee-empty">Nobenega zaposlenega še ni. Dokler ne dodate vsaj enega, ni mogoče shraniti prevzema.</p>
      ) : (
        <ul className="employee-list">
          {employees.map((employee) => (
            <li key={employee.id} className={employee.active ? "" : "employee-inactive"}>
              <span className="employee-name">
                {employee.name} {employee.lastname}
                {employee.active ? "" : " (neaktiven)"}
              </span>
              <button type="button" onClick={() => toggleMutation.mutate(employee)} disabled={toggleMutation.isPending}>
                {employee.active ? "Deaktiviraj" : "Aktiviraj"}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
